import { useEffect, useState, useCallback } from "react";
import api, { eur } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

const COLS = ["Date", "Employee", "Position", "Start", "Finish", "Total hours on Site", "Total Chargeable Hours", "Hours @ standard rate", "Hours @ Overtime Rate", "rate ex VAT", "rate incl VAT", "Total ex VAT", "Total incl VAT"];

const cleanOverrides = (o) => {
  const out = {};
  Object.entries(o || {}).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v))) out[k] = Number(v); });
  return out;
};

export default function SiteReport() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [range, setRange] = useState({ start: "", end: "" });
  const [overrides, setOverrides] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/sites").then((r) => { setSites(r.data); if (r.data[0]) setSiteId(r.data[0].id); }); }, []);

  const buildParams = useCallback((extra = {}) => {
    const params = { site_id: siteId, ...extra };
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    const ov = cleanOverrides(overrides);
    if (Object.keys(ov).length) params.overrides = JSON.stringify(ov);
    return params;
  }, [siteId, range, overrides]);

  const load = useCallback(() => {
    if (!siteId) return;
    setLoading(true);
    api.get("/reports/site-hours", { params: buildParams() })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load report"))
      .finally(() => setLoading(false));
  }, [siteId, buildParams]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const setOverride = (pos, val) => setOverrides((o) => { const n = { ...o }; if (val === "") delete n[pos]; else n[pos] = val; return n; });

  const download = async (format) => {
    if (!siteId) return;
    setBusy(format);
    try {
      const res = await api.get("/reports/site-hours", { params: buildParams({ format }), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `site-hours.${format === "xlsx" ? "xlsx" : format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch { toast.error("Export failed"); } finally { setBusy(""); }
  };

  const hasRows = data && data.groups && data.groups.length > 0;
  const hasOverrides = Object.keys(cleanOverrides(overrides)).length > 0;

  return (
    <AdminLayout nav={adminNav} title="Site Report">
      <PageHeader title="Site Hours & Charges" subtitle="Chargeable hours by site for invoicing — approved timesheets only">
        <div className="flex gap-2">
          <Button data-testid="sr-csv" variant="outline" size="sm" disabled={!hasRows || busy} onClick={() => download("csv")}><Download className="h-4 w-4 mr-1.5" />CSV</Button>
          <Button data-testid="sr-xlsx" variant="outline" size="sm" disabled={!hasRows || busy} onClick={() => download("xlsx")}><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button data-testid="sr-pdf" size="sm" className="bg-slate-900 text-white" disabled={!hasRows || busy} onClick={() => download("pdf")}><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
        </div>
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="min-w-[240px]">
          <Label>Site</Label>
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger data-testid="sr-site" className="mt-1"><SelectValue placeholder="Select site" /></SelectTrigger>
            <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>From</Label><Input data-testid="sr-start" type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="mt-1" /></div>
        <div><Label>To</Label><Input data-testid="sr-end" type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="mt-1" /></div>
      </div>

      {data && data.positions && data.positions.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-md p-3 mb-4" data-testid="sr-rate-panel">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Charge-out rates — this report only</span>
            {hasOverrides && <Button data-testid="sr-reset-rates" variant="ghost" size="sm" className="h-7 text-amber-700" onClick={() => setOverrides({})}>Reset to stored</Button>}
          </div>
          <div className="flex flex-wrap gap-4">
            {data.positions.map((p) => (
              <div key={p.position} className="flex items-center gap-1.5">
                <Label className="text-xs text-zinc-600">{p.position || "—"}</Label>
                <span className="text-xs text-zinc-400">€</span>
                <Input data-testid={`sr-rate-${p.position || "none"}`} type="number" step="0.5" className="h-8 w-20" value={overrides[p.position] ?? ""} placeholder={String(p.default_rate)} onChange={(e) => setOverride(p.position, e.target.value)} />
              </div>
            ))}
          </div>
          <p className="text-[11px] text-zinc-400 mt-2">Tweak a position's hourly charge-out rate for this report and its CSV/Excel/PDF exports. Stored rates are not changed.</p>
        </div>
      )}

      {data && (
        <div className="text-sm text-zinc-500 mb-3" data-testid="sr-meta">
          {data.site?.name}{data.site?.customer_name ? ` · ${data.site.customer_name}` : ""} · VAT {data.vat_rate}%{hasOverrides ? " · custom rates applied" : ""}
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="sr-table">
          <thead>
            <tr className="bg-zinc-50 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
              {COLS.map((c) => <th key={c} className="p-2 border-b border-zinc-200 whitespace-nowrap">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={COLS.length} className="p-6 text-center text-zinc-400">Loading…</td></tr>}
            {!loading && !hasRows && <tr><td colSpan={COLS.length} data-testid="sr-empty" className="p-6 text-center text-zinc-400">No approved chargeable hours for this site and period.</td></tr>}
            {!loading && hasRows && data.groups.map((g) => <GroupBlock key={g.date} g={g} />)}
            {!loading && hasRows && (
              <tr data-testid="sr-grand-total" className="border-t-2 border-slate-900 font-bold bg-amber-50">
                <td className="p-2" colSpan={10} />
                <td className="p-2 text-right whitespace-nowrap">TOTAL to be invoiced</td>
                <td className="p-2 num whitespace-nowrap">{eur(data.total_ex_vat)}</td>
                <td className="p-2 num whitespace-nowrap">{eur(data.total_incl_vat)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

function GroupBlock({ g }) {
  return (
    <>
      <tr className="bg-amber-50/60 border-t border-zinc-200" data-testid={`sr-date-${g.date}`}>
        <td className="p-2 num font-semibold text-slate-900 whitespace-nowrap">{g.date_label}</td>
        <td className="p-2" colSpan={COLS.length - 1} />
      </tr>
      {g.rows.map((r, i) => (
        <tr key={i} className="border-b border-zinc-50" data-testid={`sr-row-${g.date}-${i}`}>
          <td className="p-2" />
          <td className="p-2 font-medium whitespace-nowrap">{r.employee}</td>
          <td className="p-2 text-zinc-600 whitespace-nowrap">{r.position}</td>
          <td className="p-2 num whitespace-nowrap">{r.start}</td>
          <td className="p-2 num whitespace-nowrap">{r.finish}</td>
          <td className="p-2 num whitespace-nowrap">{r.hours_on_site}</td>
          <td className="p-2 num whitespace-nowrap">{r.chargeable}</td>
          <td className="p-2 num whitespace-nowrap bg-orange-50/50">{r.std_hours}</td>
          <td className="p-2 num whitespace-nowrap bg-orange-50/50">{r.ot_hours}</td>
          <td className="p-2 num whitespace-nowrap">{eur(r.rate_ex_vat)}</td>
          <td className="p-2 num whitespace-nowrap">{eur(r.rate_incl_vat)}</td>
          <td className="p-2 num whitespace-nowrap">{eur(r.total_ex_vat)}</td>
          <td className="p-2 num whitespace-nowrap">{eur(r.total_incl_vat)}</td>
        </tr>
      ))}
      <tr className="border-b border-zinc-200 font-semibold" data-testid={`sr-subtotal-${g.date}`}>
        <td className="p-2" colSpan={10} />
        <td className="p-2 text-right whitespace-nowrap">Total:</td>
        <td className="p-2 num whitespace-nowrap">{eur(g.subtotal_ex)}</td>
        <td className="p-2 num whitespace-nowrap">{eur(g.subtotal_incl)}</td>
      </tr>
    </>
  );
}
